import { GlobalGameState, GameContext, GlobalStore, GameStateActions } from '@game-libs/models';
import { GameSettings } from '@game-libs/models/src/settings.models';
import { SuperSaveSystem } from '@game-libs/super-save-system';
import { Signal, signal } from '@preact/signals';

export const GlobalGameStore = <NewGameState, NewGameActions>(): GlobalStore<NewGameState, NewGameActions> => {
  let instance: GlobalStore<NewGameState, NewGameActions> | null = (window as any).global_game_store || null;
  if (instance) {
    return instance;
  }

  const savePrefix = import.meta.env.VITE_GAME_PREFIX || 'my-game';

  const initialState: GlobalGameState = {
    context: signal(GameContext.IN_MENU),
    isPaused: signal(false),
    currentLevel: signal(''),
    globalVariables: signal({}),
  };
  let state: GlobalGameState = initialState;
  const { save: sssSave, load: sssLoad } = SuperSaveSystem();

  const initialSettings: GameSettings = {
    musicVolume: 25,
    sfxVolume: 60,
    isFullscreen: false,
    shakeIntensity: 30,
    isMuted: false,
    crtFilterEnabled: false,
    chromaticAberration: 10,
    useAlternativeFont: false,
    largerTextSize: false,
    language: 'en',
  };

  let settings: Signal<GameSettings> = signal(initialSettings);

  sssLoad(`${savePrefix}_global-settings`, settings.value).then((loadedSettings) => {
    settings.value = loadedSettings;
  });

  let actions: GameStateActions = {
    setSettings: (newSettings: Partial<GameSettings>) => {
      if (settings) {
        settings.value = {
          ...settings.value,
          ...newSettings,
        };
      }

      actions.saveSettings();
    },
    setContext: (context: GameContext) => {
      state.context.value = context;
    },
    setGlobalVariables: (variables: any) => {
      state.globalVariables.value = {
        ...state.globalVariables.value,
        ...variables,
      };

      actions.saveState();
    },
    pauseGame: () => {
      if (state.context.value === GameContext.IN_GAME) {
        state.isPaused.value = true;
      }
    },
    resumeGame: () => {
      state.isPaused.value = false;
    },
    saveState: () => {
      const saveState = Object.keys(state).reduce(
        (acc, key) => {
          acc[key as keyof GlobalGameState] = state[key as keyof GlobalGameState].value;
          return acc;
        },
        {} as Record<keyof GlobalGameState, unknown>,
      );

      sssSave(`${savePrefix}_global-save`, saveState);
    },

    setCurrentLevel: (name: string) => {
      state.currentLevel.value = name;
      actions.saveState();
    },
    saveSettings: () => {
      if (settings) {
        sssSave(`${savePrefix}_global-settings`, settings.value);
      }
    },
    loadState: async () => {
      const loadedState = await sssLoad(`${savePrefix}_global-save`, initialState);

      // Create a game state from loaded state where the values are signals
      if (loadedState) {
        const newState = Object.keys(loadedState).reduce(
          (acc, key) => {
            acc[key as keyof GlobalGameState] = signal(loadedState[key as keyof GlobalGameState]);
            return acc;
          },
          {} as Record<keyof GlobalGameState, Signal<unknown>>,
        ) as GlobalGameState;

        Object.assign(state, newState);
      } else {
        state = initialState;
      }
    },
    resetToInitialState: () => {
      // Reset all state signals to their initial values
      state.context.value = initialState.context.value;
      state.isPaused.value = initialState.isPaused.value;
      state.currentLevel.value = initialState.currentLevel.value;
      state.globalVariables.value = initialState.globalVariables.value;

      // Reset settings to initial values
      settings.value = initialSettings;

      // Save the reset state and settings
      actions.saveState();
      actions.saveSettings();
    },
  };

  // Get Save Global State from localStorage
  // Todo: this should be called in the main.ts file
  // actions.loadState();

  const addGameState = <NewGameState, NewGameActions>(gameState: NewGameState, gameActions: NewGameActions) => {
    Object.assign(state, gameState);
    Object.assign(actions, gameActions);
  };

  instance = {
    state: state as GlobalGameState & NewGameState,
    actions: actions as GameStateActions & NewGameActions,
    settings: settings as unknown as Signal<GameSettings>,
    addGameState,
  };

  (window as any).global_game_store = instance;

  return instance;
};
